import type { FC } from 'react';

import { Box, Select, type SelectChangeEvent } from '@packages/shared';
import { ArrowMediumDown32 } from '@packages/themes/icons';
import type { GenericTrackingEvent } from '@packages/tracking';
import { useTracking, useMouseflow } from '@packages/tracking';

import { defineMessages, useIntl } from 'react-intl';
import type { SortingOption } from '../../api/rest/types';
import type { PagingMode, DesktopDisplayMode } from '../../types';
import { DisplayModeEnum } from '../../types';
import { DisplaySettingsDesktopPagination } from '../DisplaySettingsDesktopPagination';

export type DisplaySettingsDesktopProps = {
  /** List of available sorting options for the product grid */
  sortingOptions: SortingOption[];
  /** Currently selected sorting key for the product grid */
  sortingKey: string;
  /** Callback fired when the sorting key for the product grid changes */
  onChangeSortingKey?: (sortingKey: string) => void;
  /** Currently selected paging mode for the product grid
   * @default 'limited'
   */
  pagingMode?: PagingMode;
  /** Number of pages */
  pageCount?: number;
  /** Callback fired when the paging mode for the product grid changes */
  onChangePagingMode?: (pagingMode: PagingMode) => void;
  /** Currently selected display mode for the product grid
   * @default 'cards'
   */
  displayMode?: DesktopDisplayMode;
  /** Callback fired when the display mode for the product grid changes */
  onChangeDisplayMode?: (displayMode: DesktopDisplayMode) => void;
};

const messages = defineMessages({
  displayModeLabel: {
    id: 'search.productList.displaySettings.displayMode.label',
    defaultMessage: 'Ansicht wechseln',
  },
  displayModeCards: {
    id: 'search.productList.displaySettings.displayModes.cards',
    defaultMessage: 'Kleine Bilder',
  },
  displayModeLargeCards: {
    id: 'search.productList.displaySettings.displayModes.largeCards',
    defaultMessage: 'Große Bilder',
  },
  displayModeMoreInformation: {
    id: 'search.productList.displaySettings.displayModes.moreInformation',
    defaultMessage: 'Mehr Informationen',
  },
  pagingModeInfinite: {
    id: 'search.productList.displaySettings.pagingMode.infinite',
    defaultMessage: 'Alle anzeigen',
  },
  pagingModeLimited: {
    id: 'search.productList.displaySettings.pagingMode.limited',
    defaultMessage: '72 Artikel pro Seite',
  },
  sortingModeLabel: {
    id: 'search.productList.displaySettings.sortingMode.label',
    defaultMessage: 'Sortieren',
  },
});

/**
 * Component to allow the user to change the display settings for the product list
 * */
export const DisplaySettingsDesktop: FC<DisplaySettingsDesktopProps> = ({
  sortingOptions,
  sortingKey,
  onChangeSortingKey,
  pageCount = 1,
  pagingMode = 'limited',
  onChangePagingMode,
  displayMode = 'cards',
  onChangeDisplayMode,
}) => {
  const { formatMessage } = useIntl();
  const dispatchGTMEvent = useTracking();

  const pushMouseflowData = useMouseflow();

  const displayModes = [
    { value: 'cards', label: formatMessage(messages.displayModeCards) },
    { value: 'largeCards', label: formatMessage(messages.displayModeLargeCards) },
    { value: 'moreInformation', label: formatMessage(messages.displayModeMoreInformation) },
  ];

  const sortingItems = sortingOptions.map(({ key: value, displayName: label }) => ({
    value,
    label,
  }));

  const pagingModeItems = [
    { value: 'limited', label: formatMessage(messages.pagingModeLimited) },
    { value: 'infinite', label: formatMessage(messages.pagingModeInfinite) },
  ];

  return (
    <Box
      sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, alignItems: 'center' }}
    >
      <Select
        IconComponent={(props) => <ArrowMediumDown32 {...props} />}
        label={formatMessage(messages.displayModeLabel)}
        items={displayModes}
        value={displayMode}
        onChange={(event: SelectChangeEvent<unknown>) => {
          onChangeDisplayMode?.(event.target.value as DesktopDisplayMode);
          dispatchGTMEvent<GenericTrackingEvent>({
            event: 'suche-event',
            eventValue: `view=${DisplayModeEnum[event.target.value as DesktopDisplayMode]}`,
          });
          switch (event.target.value as DesktopDisplayMode) {
            case 'cards':
              pushMouseflowData('search_ViewChangeSmallPictures');
              break;
            case 'moreInformation':
              pushMouseflowData('search_ViewChangeMoreInformation');
              break;
            case 'largeCards':
              pushMouseflowData('search_ViewChangeLargePictures');
              break;
            default:
          }
        }}
      />
      <Select
        IconComponent={(props) => <ArrowMediumDown32 {...props} />}
        label=""
        items={pagingModeItems}
        value={pagingMode}
        onChange={(event: SelectChangeEvent<unknown>) => {
          onChangePagingMode?.(event.target.value as PagingMode);
        }}
      />
      <Select
        IconComponent={(props) => <ArrowMediumDown32 {...props} />}
        label={formatMessage(messages.sortingModeLabel)}
        items={sortingItems}
        value={sortingKey}
        onChange={(event: SelectChangeEvent<unknown>) => {
          onChangeSortingKey?.(event.target.value as string);
          dispatchGTMEvent<GenericTrackingEvent>({
            event: 'ga-event',
            eventAction: 'Filter / Sort benutzt',
            eventCategory: 'User Interactions',
            eventLabel: 'sortiert',
          });
        }}
      />
      {pagingMode !== 'infinite' && pageCount > 1 && (
        <DisplaySettingsDesktopPagination pageCount={pageCount} />
      )}
    </Box>
  );
};
